// Shared formatting for underwriting-relevant material facts (lot size, pool, HOA) and a deep link to
// the county appraisal district — used on the Underwriting header and the MLS Opportunities detail
// panel so a value is never a bare, ambiguous "—".

// Lot size: accept the numeric sqft (preferred) or a pre-formatted text value from the deals table.
// Returns e.g. "7,200 sqft (0.17 ac)" or null when genuinely unknown.
export function fmtLotSize({ lot_size_sqft, lot_size } = {}) {
  const n = Number(lot_size_sqft);
  if (Number.isFinite(n) && n > 0) {
    const acres = n / 43560;
    const ac = acres >= 0.1 ? ` (${acres.toFixed(2)} ac)` : '';
    return `${Math.round(n).toLocaleString()} sqft${ac}`;
  }
  if (lot_size && String(lot_size).trim()) return String(lot_size).trim(); // legacy text (e.g. "7,200 sqft")
  return null;
}

// Parking display from RESO GarageSpaces (int) + ParkingFeatures (comma-joined text): "Garage · 2 car",
// "Covered", "Slab", "No garage", or null when genuinely unknown. Prefers the garage count, then the
// descriptive features, then an explicit "No garage" when the count is a real 0.
export function fmtParking({ garage_spaces, parking_features } = {}) {
  const g = Number(garage_spaces);
  const feats = String(parking_features || '').trim();
  if (Number.isFinite(g) && g > 0) return `Garage · ${g} car`;
  if (feats) return feats.split(/[,;]+/).map((s) => s.trim()).filter(Boolean).slice(0, 2).join(', ');
  if (Number.isFinite(g) && g === 0) return 'No garage';
  return null;
}

// Tri-state Yes/No/Unknown from a 1/0/null-ish flag. Treats booleans and "Yes"/"No" strings too.
export function yesNoUnknown(v) {
  if (v === 1 || v === true || v === '1' || v === 'Yes' || v === 'yes') return 'Yes';
  if (v === 0 || v === false || v === '0' || v === 'No' || v === 'no') return 'No';
  return 'Unknown';
}

// HOA display: "Yes · $22/mo", "Yes", "No", or "Unknown".
export function fmtHoa({ hoa, hoa_fee, hoa_fee_freq } = {}) {
  const base = yesNoUnknown(hoa);
  if (base === 'Yes') {
    const fee = Number(hoa_fee);
    if (Number.isFinite(fee) && fee > 0) {
      const freq = ({ Monthly: '/mo', Annually: '/yr', Quarterly: '/qtr', 'Semi-Annually': '/6mo', Weekly: '/wk' })[hoa_fee_freq]
        || (hoa_fee_freq ? ` ${hoa_fee_freq}` : '');
      return `Yes · $${Math.round(fee).toLocaleString()}${freq}`;
    }
  }
  return base;
}

// County appraisal-district property-search pages — the authoritative PUBLIC tax record for TX (the
// MLS-integrated "Realist"/ABOR-Matrix tax popup can't be deep-linked from outside: it needs Matrix's
// internal record id plus a server-computed security hash). Austin-metro counties covered; the long tail
// falls back to a scoped web search. Keyed by normalized county name.
const CAD_LINKS = {
  travis:     () => `https://property.traviscad.org/`,
  williamson: () => `https://search.wcad.org/`,
  hays:       () => `https://esearch.hayscad.com/`,
  bastrop:    () => `https://esearch.bastropcad.org/`,
  caldwell:   () => `https://esearch.caldwellcad.org/`,
  comal:      () => `https://esearch.comalad.org/`,
  guadalupe:  () => `https://esearch.guadalupead.org/`,
  burnet:     () => `https://esearch.burnetad.org/`,
  blanco:     () => `https://esearch.blancocad.com/`,
  llano:      () => `https://esearch.llanocad.net/`,
  maricopa:   (a) => `https://mcassessor.maricopa.gov/mcs/?q=${encodeURIComponent(a)}`,
};

// AZ/ARMLS agents use "Monsoon" (property-specific deep link, built below); TX/ACTRIS has no public
// Realist deep link, so it points at the county appraisal district (labeled by county). Both derive from
// the property's market/state.

// AZ county → Monsoon county code (FIPS with the leading zero dropped: Maricopa 04013 → 4013).
// Keyed by lowercased county name (no "County" suffix). Maricopa is by far the most common ARMLS county.
const AZ_COUNTY_CODE = {
  apache: '4001', cochise: '4003', coconino: '4005', gila: '4007', graham: '4009',
  greenlee: '4011', 'la paz': '4012', maricopa: '4013', mohave: '4015', navajo: '4017',
  pima: '4019', pinal: '4021', 'santa cruz': '4023', yavapai: '4025', yuma: '4027',
};

// Is this an Arizona / ARMLS (Spark) property? Prefer the explicit market, then state, then an AZ county.
function isArizona({ market, state, county }) {
  const m = String(market || '').toUpperCase();
  if (m.includes('ARMLS') || m === 'SPARK') return true;
  if (String(state || '').toUpperCase() === 'AZ') return true;
  const key = String(county || '').toLowerCase().replace(/\s+county$/, '').trim();
  return !!AZ_COUNTY_CODE[key];
}

// Build the property-specific Monsoon deep link (mymonsoon.com/Parcel/Details) from the parcel number,
// AZ county code, and the 7-digit MLS ListingId. Returns null when the parts to identify the parcel
// aren't present (→ caller falls back to the county assessor search).
function monsoonLink({ county, parcel_number, mls_number, address, city, state, zip }) {
  const pin = String(parcel_number || '').replace(/\D/g, '');   // digits only: 148-18-168 → 14818168
  const key = String(county || '').toLowerCase().replace(/\s+county$/, '').trim();
  const c = AZ_COUNTY_CODE[key];
  if (!pin || !c) return null;
  const params = new URLSearchParams({ pin, c, ls: 'armls' });
  if (mls_number) params.set('ln', String(mls_number).replace(/\D/g, '')); // ARMLS ListingId is clean digits
  params.set('adr', [address, city, state, zip].filter(Boolean).join(' '));
  return `https://mymonsoon.com/Parcel/Details?${params.toString()}`;
}

// Returns { url, label } for the tax-records link, or null if there's nothing to search on. Provider-
// aware: AZ/Spark builds a property-specific Monsoon deep link (parcel + county code + ListingId),
// TX/Grid uses the Realist label over the county appraisal district, others fall back to a county search.
export function taxRecordsLink({ county, address, city, state, zip, market, parcel_number, mls_number } = {}) {
  const addr = [address, city, state, zip].filter(Boolean).join(', ');
  if (!addr) return null;
  const key = String(county || '').toLowerCase().replace(/\s+county$/, '').trim();

  // AZ / ARMLS → property-specific Monsoon page when we can identify the parcel; else the county assessor.
  if (isArizona({ market, state, county })) {
    const deep = monsoonLink({ county, parcel_number, mls_number, address, city, state, zip });
    if (deep) return { url: deep, label: 'Monsoon tax records ↗' };
    // No parcel/county code — land on the county assessor search rather than a blank Monsoon page.
    const fallback = (key && CAD_LINKS[key])
      ? CAD_LINKS[key](addr)
      : `https://www.google.com/search?q=${encodeURIComponent(addr + ' county assessor property search')}`;
    return { url: fallback, label: 'Monsoon tax records ↗' };
  }

  // TX (and elsewhere): the county appraisal district is the authoritative PUBLIC tax record — land there
  // when we know the county, labeled by the county so the destination is honest (NOT "Realist", which
  // isn't publicly linkable). Only when the county is unknown/unmapped do we fall back to a scoped search.
  if (key && CAD_LINKS[key]) {
    const countyName = String(county || '').replace(/\s+county$/i, '').trim();
    return { url: CAD_LINKS[key](addr), label: `${countyName} County tax records ↗` };
  }
  return {
    url: `https://www.google.com/search?q=${encodeURIComponent(addr + ' county appraisal district property search')}`,
    label: 'County tax records ↗',
  };
}
